import { useEffect, useRef } from 'react';
import { day, dayAndTime, length } from './video-format.js';
import type { FamilyVideo } from '../api/family-lesson-videos.js';
import { BusyLabel } from './busy-label.js';

/**
 * The warning before a parent removes a child's video (canvas row 4, "Remove
 * tapped: a warning panel over the page", approved 17 October). Matthew: a
 * warning should be more than a string change - so the page dims and a panel
 * comes up with the video itself, his sentence, Remove and Keep.
 *
 * Keep has focus on opening, so a stray Enter keeps the video; Escape and a tap
 * on the dimmed page keep it too. Focus goes back where it was after.
 */
export function RemoveVideoSheet({
  video,
  childFirst,
  working,
  failed,
  onRemove,
  onKeep,
}: {
  video: FamilyVideo;
  childFirst: string;
  working: boolean;
  failed: boolean;
  onRemove: () => void;
  onKeep: () => void;
}) {
  const keep = useRef<HTMLButtonElement>(null);
  const keepNow = useRef(onKeep);
  keepNow.current = onKeep;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    keep.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') keepNow.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      before?.focus();
    };
  }, []);

  return (
    <div className="sheet-dim" onClick={() => !working && onKeep()}>
      <div
        className="sheet"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="remove-title"
        aria-describedby="remove-warning"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="remove-title">Remove {childFirst}&rsquo;s video?</h3>
        <div className="video-row sheet-video">
          <span className="thumb">
            {video.thumbnail_url ? <img src={video.thumbnail_url} alt="" /> : null}
            <span className="dur">{length(video.duration_seconds)}</span>
          </span>
          <div className="hour-row-text">
            <span className="name">{dayAndTime(video.sent_at)}</span>
            {video.opened_at ? <span className="detail">Opened {day(video.opened_at)}</span> : null}
          </div>
        </div>
        <p className="sheet-warning" id="remove-warning">
          This action will permanently delete this video and its associated notes
        </p>
        {failed ? (
          <p className="form-note" role="alert">
            That did not work. Try again.
          </p>
        ) : null}
        <button type="button" className="warn-fill" onClick={onRemove} disabled={working} aria-busy={working}>
          <BusyLabel busy={working} working="Removing…">
            Remove
          </BusyLabel>
        </button>
        <button ref={keep} type="button" className="secondary" onClick={onKeep} disabled={working}>
          Keep
        </button>
      </div>
    </div>
  );
}
